import { FC } from 'react';
import { useUnitPathPositionAnchor, scrollToUnitPathPosition } from '../../custom-hooks/useUnitPathPositionAnchor';
import { useUnitPathSegment } from '../../contexts/UnitPathSegmentContext';
import BackToPositionButton from './BackToPositionButton';
import TowardsMarker from './TowardsMarker';
import UnitPathSegmentLabel from './UnitPathSegmentLabel';

interface Props {
  // The target connector index from useDashboardUnitPath (first unlocked, else last passed), or null
  // when there is no connector to go to.
  positionKey: number | null;
  // Whether at least one connector has already been passed. False means the student hasn't completed
  // anything yet, so the scroll target falls back to the very top of the page instead of a connector.
  hasPassedConnector: boolean;
}

// Height of the rail's top slot, sized to the "Back to position" line so the strip below never shifts
// as the two controls swap in and out. The taller TowardsMarker avatar is centered in it and overflows
// by design, overlapping the top edge of the strip the way the design shows it.
const TOP_SLOT_CLASS_NAME = 'tw-relative tw-z-10 tw-flex tw-h-6 tw-items-center';

/**
 * The rail riding the left border of the skills area, a bit below the vertical center, that keeps the
 * student oriented while they scroll the unit path. Two stacked rows:
 *  - top: "Back to position" when their current position is off screen (pointing up or down), or the
 *    "TOWARDS" marker when they are already looking at it;
 *  - bottom: the paper strip naming the segment they are scrolled to ("Foundations" | "Target N"),
 *    read from the SAME scroll-spy instance the unit header uses (UnitPathSegmentProvider), so the two
 *    can never disagree. It takes the unit's accent color while that segment is inside the unlocked
 *    part of the path, and reads neutral once they scroll past the unlocked frontier.
 */
export const UnitPathRail: FC<Props> = ({ positionKey, hasPassedConnector }) => {
  const { state: anchorState } = useUnitPathPositionAnchor();
  const { activeSegment } = useUnitPathSegment();
  // With no connector to go to there is no position to point at, so the rail's top slot stays empty.
  const state = positionKey === null ? 'missing' : anchorState;

  return (
    <div className="tw-pointer-events-auto tw-sticky tw-top-[59vh] tw-flex tw-flex-col tw-items-start">
      <div className={TOP_SLOT_CLASS_NAME}>
        {state === 'above' || state === 'below' ? (
          <BackToPositionButton
            direction={state === 'above' ? 'up' : 'down'}
            onClick={() => scrollToUnitPathPosition(hasPassedConnector)}
          />
        ) : state === 'in-view' ? (
          <TowardsMarker />
        ) : null}
      </div>

      {activeSegment && <UnitPathSegmentLabel label={activeSegment.label} isReached={activeSegment.reached} />}
    </div>
  );
};

export default UnitPathRail;
